'use client'

import { sanitizeHTML } from '@/lib/utils'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import FadeIn from '@/components/ui/FadeIn'

interface RecoveryTimelineData {
	anchor_title?: string
	text_left_box?: {
		text?: string
	}
	text_right_box?: {
		text?: string
	}
	timeline_content_collection?: {
		id: string
		site_content_collections_id: {
			collection: {
				item: {
					title: string | null
					text: string | null
				}
			}[]
		}
	}[]
}

export const fields = [
	'anchor_title',
	'text_left_box.text',
	'text_right_box.text',
	'timeline_content_collection.id',
	'timeline_content_collection.site_content_collections_id.collection.item:site_content.title',
	'timeline_content_collection.site_content_collections_id.collection.item:site_content.text',
	'timeline_content_collection.site_content_collections_id.collection.*',
] as const

interface RecoveryTimelineProps {
	data?: RecoveryTimelineData
}

export default function RecoveryTimeline({ data }: RecoveryTimelineProps) {
	const { ref, isVisible } = useScrollAnimation({
		threshold: 0.05,
		rootMargin: '0px',
	})
	const sectionIdBase = data?.anchor_title || 'RecoveryTimeline'
	const sectionId = sectionIdBase
		.replace(/\s+/g, '-')
		.replaceAll(/[^a-zA-Z0-9-_]/g, '-')

	return (
		<section id={sectionId} data-name={data?.anchor_title || 'RecoveryTimeline'} ref={ref} className='RecoveryTimeline py-16 max-md:overflow-hidden'>
			<div className='container'>
				<div className='flex flex-col lg:flex-row gap-12'>
					{/* Left Column */}
					{data?.text_left_box?.text && (
						<FadeIn
							isVisible={isVisible}
							delay={0}
							direction='right'
							className='w-full lg:w-1/2'
						>
							<div
								className='flex flex-col gap-4 [&_h2]:mb-3'
								dangerouslySetInnerHTML={sanitizeHTML(data.text_left_box.text)}
							/>
						</FadeIn>
					)}

					{/* Right Column - Timeline */}
					<FadeIn
						isVisible={isVisible}
						delay={200}
						direction='left'
						className='w-full lg:w-1/2'
					>
						<div className='w-full h-full p-6 lg:p-10 bg-[#ddd9d6] flex flex-col gap-8'>
							{/* Timeline Header */}
							{data?.text_right_box?.text && (
								<div
									className='flex flex-col gap-4 [&_h2]:mb-3 [&_h3]:mb-3'
									dangerouslySetInnerHTML={sanitizeHTML(
										data.text_right_box.text
									)}
								/>
							)}

							{/* Timeline Items */}
							<div className='relative pr-4 md:pr-16'>
								<div className='relative grid grid-cols-1 gap-6 lg:gap-12'>
									{/* Vertical Line */}
									<div className='absolute left-1/2 top-[15px] w-[1.3px] h-[calc(100%-40px)] bg-black' />

									{/* Timeline Items */}
									{data?.timeline_content_collection?.map((item, index) => {
										const titleItem =
											item.site_content_collections_id.collection.find(
												c => c.item.title
											)
										const textItem =
											item.site_content_collections_id.collection.find(
												c => c.item.text
											)

										return (
											<FadeIn
												key={item.id}
												isVisible={isVisible}
												delay={300 + index * 100}
												direction='up'
												className='grid grid-cols-[1fr,auto,1fr] items-start gap-3 lg:gap-[21px]'
											>
												{/* Title Left */}
												<span className='text-right text-xl lg:text-3xl font-medium'>
													{titleItem?.item.title || ''}
												</span>
												{/* Dot */}
												<div className='w-4 h-4 bg-black rounded-full mt-2' />
												{/* Text Right */}
												<div
													dangerouslySetInnerHTML={sanitizeHTML(
														textItem?.item.text || ''
													)}
												/>
											</FadeIn>
										)
									})}
								</div>
							</div>
						</div>
					</FadeIn>
				</div>
			</div>
		</section>
	)
}

